import { Option } from '@sapphire/result';
import { BaseMapper, Nullable } from '@ps/utils';
import {
  DecipherLocaleMappingDto,
  DecipherMappingDto,
  DecipherTargetDto,
} from '../dtos/decipher-mapping.dto';
import {
  LocaleQuestionMappings,
  OptionMapping,
  QuestionMapping,
} from '../../state/decipher/decipher-mapping-state.model';

function isEmptyValue(value: unknown): boolean {
  return Option.from(value)
    .filter((inner) => inner !== '')
    .isNone();
}

function resolveFromTo(value: Nullable<number>): Nullable<number> {
  return isEmptyValue(value) ? null : Number(value);
}

function questionIdFromMappingId(mappingId: string): string {
  const questionId = mappingId.split(/[.=]/)[0];
  return questionId === 'list' ? 'Supplier/Vendor' : questionId;
}

function toOptionMapping(target: DecipherTargetDto): OptionMapping {
  if (target.range_sets) {
    const rangeSet = target.range_sets[0];
    return {
      kind: 'range',
      qualificationCode: target.qualification_code,
      range: {
        from: rangeSet ? resolveFromTo(rangeSet.from) : null,
        to: rangeSet ? resolveFromTo(rangeSet.to) : null,
        units: rangeSet?.units ?? null,
      },
    };
  }
  return {
    kind: 'condition',
    qualificationCode: target.qualification_code,
    conditionCodes: target.condition_codes ?? [],
  };
}

export class DecipherMappingMapper extends BaseMapper<
  DecipherLocaleMappingDto,
  LocaleQuestionMappings
> {
  public override fromDto(
    dto: DecipherLocaleMappingDto,
  ): LocaleQuestionMappings {
    return {
      locale: dto.defaultLocale ?? dto.locale,
      questionMappings: this._processData(dto.mappings),
    };
  }

  private _processData(mappings: DecipherMappingDto[]): QuestionMapping[] {
    const questionMappings: QuestionMapping[] = [];

    mappings.forEach((mapping) => {
      const decipherQuestionId = questionIdFromMappingId(mapping.mappingId);
      const existingQuestionMapping = questionMappings.find(
        (item) => item.decipherQuestionId === decipherQuestionId,
      );
      const questionMapping =
        existingQuestionMapping ??
        this._addQuestionMapping(
          questionMappings,
          decipherQuestionId,
          mapping.locale ?? '',
        );

      const isDuplicateTarget = questionMapping.targets.some(
        (target) => target.decipherCondition === mapping.mappingId,
      );
      if (isDuplicateTarget) {
        return;
      }

      const targetDtos = mapping.target ?? [];
      questionMapping.qualificationCodes.push(
        ...targetDtos.map((targetDto) => targetDto.qualification_code),
      );
      questionMapping.targets.push({
        description: mapping.desc,
        decipherCode: mapping.mappingId,
        decipherCondition: mapping.mappingId,
        optionMappings: targetDtos.map(toOptionMapping),
      });
    });

    questionMappings.forEach((questionMapping) => {
      questionMapping.qualificationCodes = [
        ...new Set(questionMapping.qualificationCodes),
      ];
    });

    return questionMappings;
  }

  private _addQuestionMapping(
    questionMappings: QuestionMapping[],
    decipherQuestionId: string,
    locale: string,
  ): QuestionMapping {
    const questionMapping: QuestionMapping = {
      decipherQuestionId,
      qualificationCodes: [],
      targets: [],
      locale,
    };
    questionMappings.push(questionMapping);
    return questionMapping;
  }
}
